import { createUseStyles } from "react-jss";
import { colors } from "../../../styles/colors";

const useStyles = createUseStyles({
  contentMain: {
    overflow: "hidden",
    height: "var(--app-vh)",
  },
  documentsRoot: {
    padding: "30px clamp(16px, 3vw, 30px)",
    overflowY: "auto",
    height: "var(--app-vh)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.bgcolor,
    /*
     * WHY THE TABLE VANISHED.
     *
     * This column is a fixed `height: var(--app-vh)` and the table card was
     * given `flex: 1 1 auto; min-height: 0` so it would absorb the leftover
     * space and scroll internally. That works while the content above it is
     * short.
     *
     * On a narrow viewport it is not short: the seven filter controls each go
     * full width, so the header, the summary chips and the filter bar alone
     * exceed the viewport. The leftover space went negative, and `flex-shrink`
     * plus `min-height: 0` let the card collapse toward zero. Inside it, the
     * scroll area collapsed the same way — leaving the search box and the
     * pagination footer (both non-shrinking) floating around an invisible
     * table. The rows were fetched and rendered the whole time; they had no
     * height to occupy, and nothing could scroll to them because the content
     * was being compressed rather than overflowing.
     *
     * So the fill-and-scroll-internally trick is applied only where it holds.
     */
    "@media (min-width:900px)": {
      "& $tableCard": {
        flex: "1 1 auto",
        minHeight: 0,
      },
    },
    "@media (max-width:899.95px)": {
      paddingTop: "68px",
      // Let the page grow and scroll normally instead of squeezing its
      // children into a viewport they cannot fit.
      height: "auto",
      minHeight: "var(--app-vh)",
    },
  },

  /*
   * Explicit hook for the table card.
   *
   * This used to be `& > div:last-child`, which silently targeted whatever
   * element happened to render last. Adding any element after the table — a
   * footnote, a banner, a conditional block — would have moved the sizing
   * rule onto it and broken the layout in a way that pointed nowhere near the
   * change that caused it.
   */
  tableCard: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    // The CustomTable root is the only child; it has to fill this wrapper for
    // the internal scroll + pinned footer to work on desktop.
    "& > *": {
      flex: "1 1 auto",
      minHeight: 0,
    },
  },
  containerHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    padding: "15px 0px 15px 0px",
  },
  summaryRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 14,
  },
  summaryChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 10,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
    color: colors.textPrimary,
    fontSize: 13,
    "& b": {
      color: colors.primary,
      fontSize: 15,
    },
  },
  /*
   * A grid, not a wrapping flex row.
   *
   * Every control was `min-width: 100%` below 900px, so seven filters became
   * seven full-width rows and pushed the table off the screen entirely. Auto
   * fitting at a 160px floor gives two or three columns on a phone and as many
   * as fit on a desktop, without a breakpoint per layout.
   */
  filtersBar: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    marginBottom: 14,
    borderRadius: 10,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
  },
  filterControl: {
    minWidth: 0, // grid children default to min-content and refuse to shrink
  },
  dateControl: {
    minWidth: 0,
  },
});

export default useStyles;
